/**
 * SSC (Single Service Calculator) API Schemas
 *
 * Zod schemas for SSC GetInsurances request and response.
 * Field names match the actual Bewotec SSC API response structure.
 */
import { z } from 'zod';
import { parseAuxiliaryAvailability } from '@/constants/extras/availability';

// ── Request Schemas ────────────────────────────────────────────────────────

export const SscServiceSchema = z.object({
  Requirement: z.string(), // "H" for Hotel
  BookingCode: z.string(), // GIATA productCode e.g. "HERIRINI119"
  ServiceTypeCode: z.string(), // roomCode + boardCode e.g. "J02H"
  From: z.string(), // /Date(ms)/ format
  To: z.string(), // /Date(ms)/ format
  Amount: z.number(), // always 1 (one room per entry)
  ParticipantAssignment: z.string(), // "1-2" format
  CatalogCode: z.string(), // e.g. "VR26"
  UserDefinedService: z.boolean(),
});

export const SscParticipantSchema = z.object({
  Salutation: z.string(), // "H" (Herr) or "D" (Dame)
  Age: z.number(),
  NoInsuranceCalcForThisParticipant: z.boolean(),
});

export const SscBasketSchema = z.object({
  Services: z.array(SscServiceSchema),
  Participants: z.array(SscParticipantSchema),
  CatalogCode: z.string(),
  Agency: z.number(),
  Language: z.string(),
});

export const GetInsurancesRequestSchema = z.object({
  Login: z.object({}),
  Basket: SscBasketSchema,
  BookingDateTime: z.string(),
  BookingSource: z.number(),
  SearchText: z.string(),
  IsInteractiveRequest: z.boolean(),
});

// ── Response Schemas ───────────────────────────────────────────────────────

export const SscPricePerParticipantSchema = z.object({
  ParticipantIndex: z.number(),
  Price: z.number(),
  Type: z.number(),
});

export const SscServiceTypeSchema = z.object({
  Code: z.string(),
  Code_Name: z.string(),
  Participants: z.number(),
  Availability: z.string(), // "*" = available, "R" = on request
  Price: z.number(), // price per person
  Total: z.number(), // total price for all participants
  Currency: z.string(),
  MinimumOccupation: z.number().optional(),
  MaximumOccupation: z.number().optional(),
  MinimumAdults: z.number().optional(),
  MaximumAdults: z.number().optional(),
  MaximumChilds: z.number().optional(),
  MaximumBabys: z.number().optional(),
  Adults: z.number().optional(),
  PricesPerParticipant: z.array(SscPricePerParticipantSchema).optional(),
  AvailabilityErrorInfo: z.unknown().nullable().optional(),
  OnlyInCombinationWith: z.record(z.unknown()).optional(),
});

export const SscInsuranceSchema = z.object({
  Requirement: z.string(), // "V" = Versicherung (insurance)
  BookingCode: z.string(), // unique insurance product code e.g. "VERGFPS"
  BookingCodeName: z.string(), // display name
  BookingCodeId: z.number(),
  CatalogueCode: z.string().optional(),
  CatalogueName: z.string().optional(),
  RequirementFlag: z.number(),
  From: z.string(),
  To: z.string(),
  ServiceTypes: z.array(SscServiceTypeSchema),
  InsuranceType: z.array(z.unknown()).optional(),
  OnlyInCombinationWith: z.record(z.unknown()).optional(),
});

export const GetInsurancesResponseSchema = z.object({
  Insurances: z.array(SscInsuranceSchema),
  ResultInfos: z.array(z.string()).optional(),
});

// ── Types ──────────────────────────────────────────────────────────────────

export type SscService = z.infer<typeof SscServiceSchema>;
export type SscParticipant = z.infer<typeof SscParticipantSchema>;
export type SscBasket = z.infer<typeof SscBasketSchema>;
export type GetInsurancesRequest = z.infer<typeof GetInsurancesRequestSchema>;
export type SscServiceType = z.infer<typeof SscServiceTypeSchema>;
export type SscInsurance = z.infer<typeof SscInsuranceSchema>;
export type GetInsurancesResponse = z.infer<typeof GetInsurancesResponseSchema>;

// ── Helpers ────────────────────────────────────────────────────────────────

/** Check if response has any insurance products */
export function hasInsurances(response: GetInsurancesResponse): boolean {
  return response.Insurances.length > 0;
}

/** Readonly-compatible insurance type for helper functions */
type ReadonlySscInsurance = {
  readonly ServiceTypes: ReadonlyArray<{
    readonly Price: number;
    readonly Total: number;
    readonly Availability: string;
  }>;
};

/**
 * Get the total price for an insurance product.
 * Total is currently always 0 from the API — we use `||` to fall back to Price.
 * Keeping Total as primary in case the API starts populating it for multi-participant scenarios.
 */
export function getInsuranceTotalPrice(insurance: ReadonlySscInsurance): number {
  if (insurance.ServiceTypes.length === 0) return 0;
  const st = insurance.ServiceTypes[0];
  return st.Total || st.Price;
}

/**
 * Get the availability status of an insurance product.
 * Uses the first ServiceType's Availability field. `'hidden'` is degraded to
 * `'unavailable'` for the display layer; filtering out hidden insurances
 * happens in `useCheckoutSsc` before mapping to display items.
 */
export function getInsuranceAvailability(
  insurance: ReadonlySscInsurance
): 'available' | 'on-request' | 'unavailable' {
  if (insurance.ServiceTypes.length === 0) return 'unavailable';
  const state = parseAuxiliaryAvailability(insurance.ServiceTypes[0].Availability);
  return state === 'hidden' ? 'unavailable' : state;
}

/**
 * Calculate `Amount` (number of insurance units) for a BA PerformAction request.
 *
 * Confirmed by Bewotec (2026-04): `Amount` is the number of service units needed
 * to cover all participants given the per-unit capacity declared in the ServiceType.
 *
 * - `Maximum*` fields (Occupation, Adults, Childs, Babys) are per-unit CAPS.
 *   A missing/zero cap means that category is UNLIMITED on a single unit, so
 *   we don't need extra units for it (e.g. VERFFB/F for families: no MaxOcc →
 *   one unit covers everyone).
 * - `MinimumOccupation` is the per-unit FLOOR. Each unit must be filled with at
 *   least that many participants, so we cap `units` at `floor(total / MinOcc)`.
 *
 * Examples for VERGFPS/VS (MinOcc=1, MaxOcc=2, MaxAdults=1, MaxChilds=1):
 *   1A→1, 1A+1C→1, 2A→2, 2A+1C→2.
 * Example for VERFFB/F (MinOcc=2, MinAdults=1, no Max*): 2A→1 (single unit covers both).
 */
export function calculateInsuranceAmount(
  serviceType: Pick<
    SscServiceType,
    'MinimumOccupation' | 'MaximumOccupation' | 'MaximumAdults' | 'MaximumChilds' | 'MaximumBabys'
  >,
  counts: { adults: number; children: number; babies?: number }
): number {
  const { MinimumOccupation, MaximumOccupation, MaximumAdults, MaximumChilds, MaximumBabys } =
    serviceType;
  const { adults, children, babies = 0 } = counts;
  const total = adults + children + babies;

  if (total === 0) return 1;

  // Per-category caps. Missing/zero = unlimited for that category → 0 units needed from it.
  const adultUnits = adults > 0 && MaximumAdults ? Math.ceil(adults / MaximumAdults) : 0;
  const childUnits = children > 0 && MaximumChilds ? Math.ceil(children / MaximumChilds) : 0;
  const babyUnits = babies > 0 && MaximumBabys ? Math.ceil(babies / MaximumBabys) : 0;
  const occUnits = MaximumOccupation ? Math.ceil(total / MaximumOccupation) : 0;

  // Smallest units that satisfy every cap; at least 1 when we have participants.
  let units = Math.max(adultUnits, childUnits, babyUnits, occUnits, 1);

  // Respect MinimumOccupation: every unit must carry at least that many participants.
  if (MinimumOccupation && MinimumOccupation > 0) {
    const maxUnitsByMin = Math.floor(total / MinimumOccupation);
    if (maxUnitsByMin >= 1 && units > maxUnitsByMin) {
      units = maxUnitsByMin;
    }
  }

  return Math.max(units, 1);
}
